<template>
    <div class="min-h-screen bg-slate-100">

        <!-- WorkMode overlay -->
        <WorkMode
            v-if="missionStarted && intervention"
            :intervention="intervention"
            @end="onMissionEnd"
        />

        <template v-else>

            <!-- ══ HEADER ════════════════════════════════════════════════════ -->
            <div class="bg-gray-900 px-4 pt-12 pb-6">
                <!-- Nav -->
                <div class="flex items-center gap-3 mb-5">
                    <button
                        class="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20"
                        @click="$router.back()"
                    >
                        <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <polyline points="15 18 9 12 15 6" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                    <span class="text-xs font-bold text-white/40 tracking-widest uppercase flex-1">Intervention</span>

                    <span
                        v-if="intervention"
                        class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
                        :class="statusClass(intervention.statut?.code)"
                    >
                        <span class="w-1.5 h-1.5 rounded-full bg-current" :class="intervention.statut?.code === 'en_cours' ? 'animate-pulse' : ''"></span>
                        {{ intervention.statut?.libelle }}
                    </span>
                </div>

                <!-- Nom + chips -->
                <div v-if="intervention">
                    <h1 class="text-3xl font-extrabold text-white leading-tight capitalize">
                        {{ intervention.client?.prenom }} {{ intervention.client?.nom }}
                    </h1>
                    <div class="flex flex-wrap gap-2 mt-3">
                        <span v-if="intervention.appareil?.app_type?.label" class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10 text-white/70 text-sm font-semibold">
                            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                            {{ intervention.appareil.app_type.label }}
                        </span>
                        <span v-if="intervention.date_intervention" class="px-3 py-1 rounded-lg bg-white/10 text-white/70 text-sm font-semibold">
                            {{ formatDate(intervention.date_intervention) }}
                        </span>
                        <span v-if="intervention.moment_day" class="px-3 py-1 rounded-lg bg-orange-500/30 text-orange-300 text-sm font-bold">
                            {{ momentDayLabels[intervention.moment_day] }}
                        </span>
                    </div>
                </div>

                <!-- Skeleton -->
                <div v-else-if="loading" class="space-y-3">
                    <div class="h-8 w-2/3 bg-white/10 rounded-xl animate-pulse"></div>
                    <div class="h-5 w-1/3 bg-white/10 rounded-xl animate-pulse"></div>
                </div>
            </div>

            <!-- ══ LOADING ════════════════════════════════════════════════ -->
            <div v-if="loading && !intervention" class="flex flex-col items-center justify-center py-24 gap-3">
                <div class="w-8 h-8 border-2 border-slate-300 border-t-orange-500 rounded-full animate-spin"></div>
                <p class="text-slate-500 text-sm font-medium">Chargement…</p>
            </div>

            <!-- ══ ERREUR ═════════════════════════════════════════════════ -->
            <div v-else-if="error && !intervention" class="mx-4 mt-6 bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
                <p class="text-red-700 font-bold text-base">Impossible de charger l'intervention</p>
                <p class="text-red-500 text-sm mt-1">{{ error }}</p>
                <button class="mt-4 px-5 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl" @click="loadData">Réessayer</button>
            </div>

            <!-- ══ CONTENU ════════════════════════════════════════════════ -->
            <div v-else-if="intervention" class="px-4 py-4 space-y-3 pb-36">

                <!-- Notes -->
                <div v-if="intervention.notes_internes" class="bg-amber-50 border border-amber-100 p-3 rounded-xl">
                    <div class="flex items-start gap-2 mb-3 border-b border-amber-700 pb-3">
                        <i class="fas fa-comment text-amber-700 text-sm mt-0.5"></i>
                        <div class="text-amber-700 font-bold">Notes internes</div>
                    </div>
                    <p class="text-amber-700 line-clamp-2">{{ intervention.notes_internes }}</p>
                </div>

                <!-- Régie -->
                <div v-if="intervention.regie" class="bg-white rounded-2xl shadow-sm p-4">
                    <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Régie</p>
                    <div class="flex items-center gap-3">
                        <div class="w-11 h-11 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0">
                            <span class="text-base font-black text-indigo-600 uppercase">
                                {{ (intervention.regie.prenom?.[0] ?? '') }}{{ (intervention.regie.nom?.[0] ?? '') }}
                            </span>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-base font-bold text-slate-900 capitalize">{{ intervention.regie.prenom }} {{ intervention.regie.nom }}</p>
                            <p v-if="intervention.regie.email" class="text-sm text-slate-500 truncate mt-0.5">{{ intervention.regie.email }}</p>
                        </div>
                        <a v-if="intervention.regie.telephone" :href="`tel:${intervention.regie.telephone}`"
                           class="w-11 h-11 bg-green-50 border border-green-200 rounded-xl flex items-center justify-center shrink-0 active:bg-green-100">
                            <svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                        </a>
                    </div>
                </div>

                <!-- Propriétaire -->
                <div v-if="!intervention.regie" class="bg-white rounded-2xl shadow-sm p-4">
                    <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Propriétaire</p>
                    <div class="flex items-center gap-3">
                        <div class="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center shrink-0">
                            <span class="text-base font-black text-orange-600 uppercase">
                                {{ (intervention.client?.prenom?.[0] ?? '') }}{{ (intervention.client?.nom?.[0] ?? '') }}
                            </span>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-base font-bold text-slate-900 capitalize">{{ intervention.client?.prenom }} {{ intervention.client?.nom }}</p>
                            <p v-if="intervention.client?.email" class="text-sm text-slate-500 truncate mt-0.5">{{ intervention.client.email }}</p>
                        </div>
                        <a v-if="intervention.client?.telephone" :href="`tel:${intervention.client.telephone}`"
                           class="w-11 h-11 bg-green-50 border border-green-200 rounded-xl flex items-center justify-center shrink-0 active:bg-green-100">
                            <svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                        </a>
                    </div>
                </div>

                <!-- Locataire -->
                <div v-if="intervention.locataire" class="bg-white rounded-2xl shadow-sm p-4">
                    <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Locataire</p>
                    <div class="flex items-center gap-3">
                        <div class="w-11 h-11 rounded-2xl bg-teal-100 flex items-center justify-center shrink-0">
                            <span class="text-base font-black text-teal-600 uppercase">
                                {{ (intervention.locataire.prenom?.[0] ?? '') }}{{ (intervention.locataire.nom?.[0] ?? '') }}
                            </span>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-base font-bold text-slate-900 capitalize">{{ intervention.locataire.prenom }} {{ intervention.locataire.nom }}</p>
                            <p v-if="intervention.locataire.email" class="text-sm text-slate-500 truncate mt-0.5">{{ intervention.locataire.email }}</p>
                        </div>
                        <a v-if="intervention.locataire.telephone" :href="`tel:${intervention.locataire.telephone}`"
                           class="w-11 h-11 bg-green-50 border border-green-200 rounded-xl flex items-center justify-center shrink-0 active:bg-green-100">
                            <svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                        </a>
                    </div>
                </div>

                <!-- Équipement -->
                <div v-if="intervention.appareil" class="bg-white rounded-2xl shadow-sm p-4">
                    <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Équipement</p>
                    <div class="grid grid-cols-2 gap-2">
                        <div class="bg-slate-50 rounded-xl p-3">
                            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Type</p>
                            <p class="text-sm font-bold text-slate-800">{{ intervention.appareil.app_type?.label || '—' }}</p>
                        </div>
                        <div class="bg-slate-50 rounded-xl p-3">
                            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Marque</p>
                            <p class="text-sm font-bold text-slate-800">{{ intervention.appareil.marque || '—' }}</p>
                        </div>
                        <div class="bg-slate-50 rounded-xl p-3">
                            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Modèle</p>
                            <p class="text-sm font-bold text-slate-800">{{ intervention.appareil.model || '—' }}</p>
                        </div>
                        <div class="bg-slate-50 rounded-xl p-3">
                            <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">N° série</p>
                            <p class="text-sm font-bold text-slate-800 font-mono tracking-wide">{{ intervention.appareil.numero_serie || '—' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Adresse -->
                <div v-if="intervention.adresse" class="bg-white rounded-2xl shadow-sm p-4">
                    <p class="text-[10px] font-black text-slate-400 tracking-widest uppercase mb-3">Adresse</p>

                    <div class="flex items-start gap-3 mb-4">
                        <div class="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
                            <svg class="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="text-base font-bold text-slate-900 leading-snug">{{ intervention.adresse.adresse1 }}</p>
                            <p class="text-sm text-slate-500 mt-0.5">{{ intervention.adresse.code_postal }} {{ intervention.adresse.ville }}</p>
                        </div>
                    </div>

                    <!-- Infos complémentaires -->
                    <div
                        v-if="intervention.adresse.adresse2 || intervention.adresse.acces || intervention.adresse.notes || intervention.adresse.nom_contact || intervention.adresse.telephone"
                        class="bg-slate-50 rounded-xl overflow-hidden mb-4 divide-y divide-slate-100"
                    >
                        <div v-if="intervention.adresse.adresse2" class="flex justify-between gap-3 px-3 py-2.5">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Complément</span>
                            <span class="text-sm text-slate-700 font-medium text-right">{{ intervention.adresse.adresse2 }}</span>
                        </div>
                        <div v-if="intervention.adresse.acces" class="flex justify-between gap-3 px-3 py-2.5">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Accès</span>
                            <span class="text-sm text-slate-700 font-medium text-right">{{ intervention.adresse.acces }}</span>
                        </div>
                        <div v-if="intervention.adresse.notes" class="flex justify-between gap-3 px-3 py-2.5">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Notes</span>
                            <span class="text-sm text-slate-700 font-medium text-right">{{ intervention.adresse.notes }}</span>
                        </div>
                        <div v-if="intervention.adresse.nom_contact" class="flex justify-between gap-3 px-3 py-2.5">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Contact</span>
                            <span class="text-sm text-slate-700 font-medium text-right">{{ intervention.adresse.nom_contact }}</span>
                        </div>
                        <div v-if="intervention.adresse.telephone" class="flex justify-between gap-3 px-3 py-2.5">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">Tél.</span>
                            <span class="text-sm text-slate-700 font-medium text-right">{{ intervention.adresse.telephone }}</span>
                        </div>
                    </div>

                    <!-- Nav GPS -->
                    <div class="grid grid-cols-2 gap-2">
                        <a :href="wazeUrl" target="_blank"
                           class="flex items-center justify-center gap-2 py-3 bg-sky-50 border border-sky-200 rounded-xl active:bg-sky-100">
                            <img src="https://upload.wikimedia.org/wikipedia/fr/thumb/e/e5/Waze_logo-2012.svg/960px-Waze_logo-2012.svg.png" class="w-5 h-5" />
                            <span class="text-sm font-bold text-sky-700">Waze</span>
                        </a>
                        <a :href="googleMapsUrl" target="_blank"
                           class="flex items-center justify-center gap-2 py-3 bg-slate-50 border border-slate-200 rounded-xl active:bg-slate-100">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/Google_Maps_icon_%282026%29.svg/3840px-Google_Maps_icon_%282026%29.svg.png" class="w-5 h-5" />
                            <span class="text-sm font-bold text-slate-700">Google Maps</span>
                        </a>
                    </div>
                </div>

            </div>

            <!-- ══ FOOTER ════════════════════════════════════════════════ -->
            <div
                v-if="intervention"
                class="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 pt-3"
                :style="`padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px))`"
            >
                <div class="flex gap-3">
                    <button
                        v-if="canCancel"
                        class="flex items-center gap-2 px-4 py-3.5 bg-red-50 border-2 border-red-200 text-red-600 text-sm font-bold rounded-2xl active:bg-red-100 shrink-0"
                        @click="showCancelModal = true"
                    >
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                        Annuler
                    </button>
                    <button
                        v-if="canStart"
                        class="flex-1 flex items-center justify-center gap-2 py-3.5 bg-orange-500 text-white text-base font-extrabold rounded-2xl active:bg-orange-600 disabled:opacity-50"
                        :disabled="starting"
                        @click="startIntervention"
                    >
                        <span v-if="starting" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                        <svg v-else class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        {{ starting ? 'Démarrage…' : 'Démarrer l\'intervention' }}
                    </button>
                </div>
            </div>

        </template>

        <!-- ══ MODAL ANNULATION ══════════════════════════════════════════ -->
        <transition name="slide-up">
            <div v-if="showCancelModal" class="fixed inset-0 z-50 flex items-end bg-black/50" @click.self="showCancelModal = false">
                <div
                    class="w-full bg-white rounded-t-3xl pt-3 px-4 max-h-[85vh] flex flex-col"
                    :style="`padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px))`"
                >
                    <div class="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4 shrink-0"></div>

                    <div class="flex items-center justify-between mb-4 shrink-0">
                        <p class="text-lg font-extrabold text-slate-900">Annuler l'intervention</p>
                        <button class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" @click="showCancelModal = false">
                            <svg class="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <div class="overflow-y-auto flex-1 space-y-2 mb-3">
                        <button
                            v-for="m in cancelMotifs"
                            :key="m"
                            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all"
                            :class="cancelMotif === m ? 'border-red-400 bg-red-50 text-red-700' : 'border-slate-200 bg-white text-slate-700'"
                            @click="cancelMotif = m"
                        >
                            <span
                                class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
                                :class="cancelMotif === m ? 'border-red-500 bg-red-500' : 'border-slate-300'"
                            >
                                <svg v-if="cancelMotif === m" class="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                            <span class="text-sm font-semibold">{{ m }}</span>
                        </button>
                    </div>

                    <textarea
                        v-model="cancelMotif"
                        class="w-full border-2 border-slate-200 focus:border-orange-400 rounded-xl px-3 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none resize-none mb-3 shrink-0"
                        rows="2"
                        placeholder="Ou saisissez un motif personnalisé…"
                    />

                    <button
                        class="w-full py-4 bg-red-500 text-white text-base font-extrabold rounded-2xl disabled:opacity-40 active:bg-red-600 shrink-0 flex items-center justify-center gap-2"
                        :disabled="!cancelMotif.trim() || cancelling"
                        @click="cancelIntervention"
                    >
                        <span v-if="cancelling" class="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                        {{ cancelling ? 'Annulation…' : 'Confirmer l\'annulation' }}
                    </button>
                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import WorkMode from './Intervention.vue';

export default {
    name: 'InterventionDetail',
    components: { WorkMode },

    data() {
        return {
            loading: true,
            error: null,
            intervention: null,
            missionStarted: false,
            starting: false,
            cancelling: false,
            showCancelModal: false,
            cancelMotif: '',
            cancelMotifs: [
                'Client absent',
                'Client injoignable',
                'Accès impossible',
                'Client a refusé l\'intervention',
                'Pièce manquante',
                'Mauvaise pièce commandée',
                'Équipement inaccessible',
                'Installation non conforme',
                'Risque électrique détecté',
                'Fuite de gaz détectée',
                'Conditions de sécurité non réunies',
                'Erreur de planning',
                'Intervention annulée par le client',
                'Contrat expiré',
            ],
            momentDayLabels: {
                morning: 'Matin',
                afternoon: 'Après-midi',
                day: 'Journée',
            },
        };
    },

    computed: {
        interventionId() { return this.$route.params.id; },
        cacheKeyDetail()  { return `intervention-${this.interventionId}`; },
        cacheKeyTravaux() { return `travaux-${this.interventionId}`; },
        cacheKeyDraft()   { return `intervention-form-${this.interventionId}`; },

        canStart() {
            if (!this.intervention) return false;
            const code = this.intervention.statut?.code;
            return code === 'planifie' || code === 'a_planifier';
        },
        canCancel() {
            if (!this.intervention) return false;
            const code = this.intervention.statut?.code;
            return code === 'planifie' || code === 'a_planifier';
        },

        wazeUrl() {
            const a = this.intervention?.adresse;
            if (!a) return '#';
            if (a.latitude && a.longitude) return `https://waze.com/ul?ll=${a.latitude},${a.longitude}&navigate=yes`;
            return `https://waze.com/ul?q=${encodeURIComponent(`${a.adresse1} ${a.code_postal} ${a.ville}`)}&navigate=yes`;
        },
        googleMapsUrl() {
            const a = this.intervention?.adresse;
            if (!a) return '#';
            const dest = a.latitude && a.longitude
                ? `${a.latitude},${a.longitude}`
                : encodeURIComponent(`${a.adresse1} ${a.code_postal} ${a.ville}`);
            return `https://maps.google.com/?daddr=${dest}&dirflg=d`;
        },
    },

    created() { this.loadData(); },

    methods: {
        async loadData() {
            this.loading = true;
            this.error = null;
            try {
                const cached = localStorage.getItem(this.cacheKeyDetail);
                if (cached) this.intervention = JSON.parse(cached);
            } catch {}

            const hasDraft = !!localStorage.getItem(this.cacheKeyDraft);

            try {
                const res = await this.$request.get(
                    `/interventions/${this.interventionId}?with_travaux=1&with_chantier=1&with_regie=1&with_locataire=1`,
                    {}
                );
                if (res) {
                    this.intervention = res;
                    try { localStorage.setItem(this.cacheKeyDetail, JSON.stringify(res)); } catch {}
                }
            } catch (e) {
                if (!this.intervention) this.error = e?.message || 'Erreur réseau';
            }

            if (hasDraft || this.intervention?.statut?.code === 'en_cours') {
                this.missionStarted = true;
            }
            this.loading = false;
        },

        async startIntervention() {
            if (!confirm('Confirmer le démarrage de l\'intervention ?')) return;
            this.starting = true;
            try {
                await this.$request.post(`/interventions/${this.interventionId}/demarrer`, {});
                if (this.intervention) {
                    this.intervention = { ...this.intervention, statut: { code: 'en_cours', libelle: 'En cours' } };
                    try { localStorage.setItem(this.cacheKeyDetail, JSON.stringify(this.intervention)); } catch {}
                }
                this.missionStarted = true;
            } catch (e) {
                alert(e?.message || 'Impossible de démarrer l\'intervention');
            } finally {
                this.starting = false;
            }
        },

        async cancelIntervention() {
            if (!this.cancelMotif.trim()) return;
            this.cancelling = true;
            try {
                await this.$request.post(`/interventions/${this.interventionId}/annuler`, { motif: this.cancelMotif });
                this.showCancelModal = false;
                this.clearLocalStorage();
                this.$router.replace('/');
            } catch (e) {
                alert(e?.message || 'Impossible d\'annuler l\'intervention');
            } finally {
                this.cancelling = false;
            }
        },

        onMissionEnd() {
            this.clearLocalStorage();
            this.$router.replace('/');
        },

        clearLocalStorage() {
            try {
                localStorage.removeItem(this.cacheKeyDetail);
                localStorage.removeItem(this.cacheKeyTravaux);
                localStorage.removeItem(this.cacheKeyDraft);
                localStorage.removeItem(`intervention:${this.interventionId}:pv_reception`);
                localStorage.removeItem(`intervention:${this.interventionId}:attestation_entretien`);
                localStorage.removeItem(`intervention:${this.interventionId}:contrat_entretien`);
                localStorage.removeItem(`cerfa_draft_${this.interventionId}`);
            } catch {}
        },

        statusClass(code) {
            const map = {
                planifie:    'bg-orange-100 text-orange-700',
                a_planifier: 'bg-orange-100 text-orange-700',
                en_cours:    'bg-green-100 text-green-700',
                termine:     'bg-slate-100 text-slate-600',
                annule:      'bg-red-100 text-red-600',
            };
            return map[code] ?? 'bg-slate-100 text-slate-600';
        },

        formatDate(str) {
            if (!str) return '';
            return new Date(str).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
        },
    },
};
</script>

<style scoped>
.slide-up-enter-active { transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
.slide-up-leave-active { transition: all 0.2s ease-in; }
.slide-up-enter-from, .slide-up-leave-to { opacity: 0; transform: translateY(100%); }
</style>